// News Clusters admin view. Surfaces the embedding-based grouping
// produced by the boot-time cluster worker. Three endpoints feed this
// page:
//   GET /api/admin/clusters?scope=&ticker=&verdict=&status=&limit=
//                                                  — list view
//   GET /api/admin/clusters/{id}                   — detail modal
//   GET /api/admin/cluster-actions?limit=          — Phase 3 feed
//
// Operator workflow: scan the list for a ticker spike (item_count
// climbing fast on a single cluster) → click "보기" → eyeball the
// member headlines and sentiment_avg before acting. The Phase 3 side
// panel shows split/merge actions performed automatically by the
// orchestrator so the operator can audit recent state changes.
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  api,
  type ClusterAction,
  type NewsCluster,
  type NewsClusterMember,
} from '@/api/client'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
} from '@/admin-design/_common'
import { formatRelativeKST, formatTime } from '@/lib/utils'
import { krxName } from '@/lib/krx-names'

type ScopeFilter = 'all' | 'ticker' | 'market' | 'crypto'
type VerdictFilter = 'all' | 'keep' | 'split' | 'merge'
type StatusFilter = 'active' | 'archived' | 'all'

export default function Clusters() {
  const [scope, setScope] = useState<ScopeFilter>('all')
  const [tickerInput, setTickerInput] = useState('')
  const [tickerFilter, setTickerFilter] = useState('')
  const [verdict, setVerdict] = useState<VerdictFilter>('all')
  const [status, setStatus] = useState<StatusFilter>('active')
  const [selected, setSelected] = useState<number | null>(null)

  const list = useQuery({
    queryKey: ['clusters', 'list', scope, tickerFilter, verdict, status],
    queryFn: () =>
      api.clustersList({
        scope: scope === 'all' ? undefined : scope,
        ticker: tickerFilter || undefined,
        verdict: verdict === 'all' ? undefined : verdict,
        status,
        limit: 100,
      }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  })

  const actions = useQuery({
    queryKey: ['cluster-actions'],
    queryFn: () => api.clusterActions({ limit: 30 }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: false,
  })

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">Clusters</h1>
          <p className="text-sm text-muted-foreground">
            BGE-M3 임베딩 기반 뉴스 클러스터링. 종목별로 같은 스토리에
            속한 뉴스를 묶어 보여줍니다 — 60초마다 자동 갱신.
          </p>
        </div>
      </header>

      <Card>
        <Card.Body className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Scope</label>
            <Select
              value={scope}
              onChange={(e) => setScope(e.target.value as ScopeFilter)}
              className="w-36"
              options={[
                { value: 'all', label: '전체' },
                { value: 'ticker', label: 'ticker' },
                { value: 'market', label: 'market' },
                { value: 'crypto', label: 'crypto' },
              ]}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Status</label>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as StatusFilter)}
              className="w-36"
              options={[
                { value: 'active', label: 'Active (기본)' },
                { value: 'archived', label: 'Archived (자동 정리)' },
                { value: 'all', label: '전체' },
              ]}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Verdict</label>
            <Select
              value={verdict}
              onChange={(e) => setVerdict(e.target.value as VerdictFilter)}
              className="w-36"
              options={[
                { value: 'all', label: '전체' },
                { value: 'keep', label: 'keep' },
                { value: 'split', label: 'split (분리 후보)' },
                { value: 'merge', label: 'merge' },
              ]}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-muted-foreground">Ticker</label>
            <div className="flex items-center gap-2">
              <Input
                type="text"
                placeholder="예: 005930"
                value={tickerInput}
                onChange={(e) => setTickerInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') setTickerFilter(tickerInput.trim())
                }}
                className="w-36"
              />
              <Button
                size="sm"
                variant="secondary"
                onClick={() => setTickerFilter(tickerInput.trim())}
              >
                필터
              </Button>
              {tickerFilter && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    setTickerInput('')
                    setTickerFilter('')
                  }}
                >
                  초기화
                </Button>
              )}
            </div>
          </div>
        </Card.Body>
      </Card>

      {list.isPending && (
        <Card>
          <Card.Body>
            <p className="text-sm text-muted-foreground">로딩 중…</p>
          </Card.Body>
        </Card>
      )}
      {list.error && (
        <Card className="border-destructive-soft bg-destructive-soft">
          <Card.Body className="text-sm text-destructive-soft-foreground">
            로드 실패: {String(list.error)}
          </Card.Body>
        </Card>
      )}

      {list.data && list.data.clusters.length === 0 && (
        <Card>
          <Card.Body>
            <EmptyState
              title="클러스터가 없습니다"
              description="아직 임베딩된 뉴스가 없거나, 필터 조건에 맞는 클러스터가 없습니다. 워커가 5–15분마다 자동으로 채웁니다."
            />
          </Card.Body>
        </Card>
      )}

      <div className="grid gap-4 xl:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          {list.data && list.data.clusters.length > 0 && (
            <Card>
              <Card.Body className="overflow-x-auto p-0">
                <table className="w-full text-sm">
                  <thead className="border-b border-border bg-muted/40 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">ID</th>
                      <th className="px-3 py-2 text-left font-medium">Scope</th>
                      <th className="px-3 py-2 text-left font-medium">Ticker</th>
                      <th className="px-3 py-2 text-right font-medium">아이템</th>
                      <th className="px-3 py-2 text-left font-medium">감성</th>
                      <th className="px-3 py-2 text-left font-medium">품질</th>
                      <th className="px-3 py-2 text-left font-medium">Verdict</th>
                      <th className="px-3 py-2 text-left font-medium">요약</th>
                      <th className="px-3 py-2 text-right font-medium">최근</th>
                      <th className="px-3 py-2 text-right font-medium"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.data.clusters.map((c) => (
                      <ClusterRow
                        key={c.id}
                        cluster={c}
                        onView={() => setSelected(c.id)}
                      />
                    ))}
                  </tbody>
                </table>
              </Card.Body>
            </Card>
          )}
        </div>

        <div className="space-y-3">
          <ActionsPanel
            actions={actions.data?.actions ?? []}
            isLoading={actions.isPending}
            error={actions.error}
          />
        </div>
      </div>

      {selected !== null && (
        <ClusterDetailModal id={selected} onClose={() => setSelected(null)} />
      )}
    </div>
  )
}

// ActionsPanel renders the most-recent Phase 3 split + merge actions
// from cluster_actions. Each row shows the action badge (split=
// destructive, merge=primary), the cluster id(s) involved, the cosine
// similarity (merge only), and a relative timestamp. The reason text
// from the LLM judge is rendered as a tooltip so the operator can hover
// to learn why an action fired without losing scroll position.
function ActionsPanel({
  actions,
  isLoading,
  error,
}: {
  actions: ClusterAction[]
  isLoading: boolean
  error: unknown
}) {
  return (
    <Card>
      <Card.Header>
        <Card.Title>최근 자동 액션</Card.Title>
        <p className="text-xs text-muted-foreground">
          Phase 3 (auto split/merge) 가 실행한 직전 액션 30개. 60초마다 갱신.
        </p>
      </Card.Header>
      <Card.Body className="space-y-2">
        {isLoading && (
          <p className="text-sm text-muted-foreground">로딩 중…</p>
        )}
        {error ? (
          <p className="text-sm text-destructive">
            로드 실패: {String(error)}
          </p>
        ) : null}
        {!isLoading && !error && actions.length === 0 && (
          <p className="text-sm text-muted-foreground">
            아직 자동 액션이 없습니다. Phase 3 가 켜지면 여기 표시됩니다.
          </p>
        )}
        {actions.length > 0 && (
          <ul className="space-y-2">
            {actions.map((a) => (
              <ActionRow key={a.id} action={a} />
            ))}
          </ul>
        )}
      </Card.Body>
    </Card>
  )
}

function ActionRow({ action }: { action: ClusterAction }) {
  const variant = action.action === 'split' ? 'destructive' : 'info'
  const label = action.action === 'split' ? 'split' : 'merge'
  return (
    <li
      className="rounded-md border border-border bg-card p-2 text-xs"
      title={action.reason}
    >
      <div className="mb-1 flex items-center gap-2">
        <Badge variant={variant} shape="pill">
          {label}
        </Badge>
        <span className="font-mono text-muted-foreground">
          #{action.primary_cluster_id}
          {action.secondary_cluster_id !== undefined &&
            action.secondary_cluster_id !== null && (
              <>
                {' ← '}#{action.secondary_cluster_id}
              </>
            )}
        </span>
        <span
          className="ml-auto text-muted-foreground"
          title={formatTime(action.created_at)}
        >
          {formatRelativeKST(action.created_at)}
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span>아이템 {action.member_count}건</span>
        {action.cosine_similarity !== undefined &&
          action.cosine_similarity !== null && (
            <span>cosine {action.cosine_similarity.toFixed(3)}</span>
          )}
        {action.quality_score !== undefined &&
          action.quality_score !== null && (
            <span>품질 {action.quality_score}/5</span>
          )}
      </div>
    </li>
  )
}

function ClusterRow({
  cluster,
  onView,
}: {
  cluster: NewsCluster
  onView: () => void
}) {
  const tickerLabel = cluster.ticker
    ? cluster.ticker + (krxName(cluster.ticker) ? ` ${krxName(cluster.ticker)}` : '')
    : '—'
  // Archived rows render muted so the operator's eye lands on
  // active clusters first when status='all' is selected. The text-
  // muted-foreground + bg-card/50 combo follows the admin-design
  // semantic-token rule (no raw Tailwind colors).
  const archived = cluster.status === 'archived'
  const rowClass = archived
    ? 'border-b border-border last:border-0 bg-card/50 text-muted-foreground hover:bg-muted/30'
    : 'border-b border-border last:border-0 hover:bg-muted/30'
  return (
    <tr className={rowClass}>
      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">
        #{cluster.id}
        {archived && (
          <span
            className="ml-1 text-[10px]"
            title={cluster.archive_reason || 'archived'}
          >
            (archived)
          </span>
        )}
      </td>
      <td className="px-3 py-2">
        <Badge variant={scopeVariant(cluster.scope)} shape="pill">
          {cluster.scope}
        </Badge>
      </td>
      <td className="px-3 py-2 font-mono text-xs">{tickerLabel}</td>
      <td className="px-3 py-2 text-right font-medium">{cluster.item_count}</td>
      <td className="px-3 py-2">
        {cluster.sentiment ? (
          <div className="flex items-center gap-1.5">
            <SentimentBadge value={cluster.sentiment} />
            {cluster.sentiment_score !== undefined && cluster.sentiment_score !== null && (
              <SentimentScoreCell value={cluster.sentiment_score} />
            )}
          </div>
        ) : (
          <SentimentAvgCell value={cluster.sentiment_avg} />
        )}
      </td>
      <td className="px-3 py-2">
        <QualityBadge score={cluster.quality_score} />
      </td>
      <td className="px-3 py-2">
        <VerdictBadge verdict={cluster.quality_verdict} reason={cluster.quality_reason} />
      </td>
      <td className="px-3 py-2 text-xs">
        {cluster.summary_ko ? (
          <span className="line-clamp-2 text-foreground">{cluster.summary_ko}</span>
        ) : cluster.label ? (
          <span className="text-muted-foreground">{cluster.label}</span>
        ) : (
          <span className="text-muted-foreground">(요약 대기)</span>
        )}
      </td>
      <td
        className="px-3 py-2 text-right text-xs text-muted-foreground"
        title={formatTime(cluster.last_seen_at)}
      >
        {formatRelativeKST(cluster.last_seen_at)}
      </td>
      <td className="px-3 py-2 text-right">
        <Button size="sm" variant="secondary" onClick={onView}>
          보기
        </Button>
      </td>
    </tr>
  )
}

// QualityBadge renders the LLM-judge composite score (1..5). NULL
// quality_score (= judge hasn't run yet) shows "미평가" so the operator
// can distinguish "not yet judged" from "judged low".
function QualityBadge({ score }: { score?: number }) {
  if (score === undefined || score === null) {
    return <span className="text-xs text-muted-foreground">미평가</span>
  }
  // Higher score = tighter cluster. Map to badge variants:
  //   5 → success (green-soft)
  //   3-4 → muted/info
  //   1-2 → destructive (red-soft)
  let variant: 'success' | 'info' | 'muted' | 'warning' | 'destructive'
  if (score >= 5) variant = 'success'
  else if (score >= 4) variant = 'info'
  else if (score >= 3) variant = 'muted'
  else if (score >= 2) variant = 'warning'
  else variant = 'destructive'
  return (
    <Badge variant={variant} shape="square" title={`품질 ${score}/5`}>
      {score}/5
    </Badge>
  )
}

// VerdictBadge renders keep/split/merge with reason as a tooltip. Empty
// verdict (= judge hasn't scored yet) renders "—" so the column never
// looks broken on pre-Phase-2 rows.
function VerdictBadge({
  verdict,
  reason,
}: {
  verdict?: string
  reason?: string
}) {
  if (!verdict) {
    return <span className="text-xs text-muted-foreground">—</span>
  }
  const map: Record<
    string,
    { label: string; variant: 'success' | 'destructive' | 'muted' | 'warning' | 'info' }
  > = {
    keep: { label: 'keep', variant: 'success' },
    split: { label: 'split', variant: 'destructive' },
    merge: { label: 'merge', variant: 'warning' },
  }
  const m = map[verdict] ?? { label: verdict, variant: 'muted' as const }
  return (
    <Badge variant={m.variant} shape="pill" title={reason || m.label}>
      {m.label}
    </Badge>
  )
}

function SentimentAvgCell({ value }: { value?: number }) {
  if (value === undefined || value === null) {
    return <span className="text-muted-foreground">—</span>
  }
  const tone =
    value > 0.2 ? 'text-success' : value < -0.2 ? 'text-destructive' : 'text-foreground'
  const sign = value > 0 ? '+' : ''
  return <span className={`font-mono ${tone}`}>{sign}{value.toFixed(2)}</span>
}

// SentimentScoreCell renders the LLM-derived cluster sentiment_score as
// a compact monospace badge. Mirrors SentimentAvgCell's tone rules but
// fed by the cluster summary path (vs the running member-row mean).
function SentimentScoreCell({ value }: { value: number }) {
  const tone =
    value > 0.2 ? 'text-success' : value < -0.2 ? 'text-destructive' : 'text-muted-foreground'
  const sign = value > 0 ? '+' : ''
  return (
    <span className={`font-mono text-xs ${tone}`}>
      {sign}
      {value.toFixed(2)}
    </span>
  )
}

function ClusterDetailModal({
  id,
  onClose,
}: {
  id: number
  onClose: () => void
}) {
  const detail = useQuery({
    queryKey: ['clusters', 'detail', id],
    queryFn: () => api.clusterDetail(id),
    refetchOnWindowFocus: false,
  })

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-3xl">
        <Card>
          <Card.Header className="flex items-center justify-between gap-2">
            <Card.Title>클러스터 #{id}</Card.Title>
            <Button size="sm" variant="secondary" onClick={onClose}>
              닫기
            </Button>
          </Card.Header>
          <Card.Body className="max-h-[70vh] overflow-y-auto">
            {detail.isPending && (
              <p className="text-sm text-muted-foreground">로딩 중…</p>
            )}
            {detail.error && (
              <p className="text-sm text-destructive">
                로드 실패: {String(detail.error)}
              </p>
            )}
            {detail.data && (
              <ClusterDetailBody
                cluster={detail.data.cluster}
                items={detail.data.items}
              />
            )}
          </Card.Body>
        </Card>
      </div>
    </div>
  )
}

function ClusterDetailBody({
  cluster,
  items,
}: {
  cluster: NewsCluster
  items: NewsClusterMember[]
}) {
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3 rounded-md border border-border bg-muted/40 p-3 text-xs">
        <Badge variant={scopeVariant(cluster.scope)} shape="pill">
          {cluster.scope}
        </Badge>
        {cluster.ticker && (
          <span className="font-mono">
            {cluster.ticker}
            {krxName(cluster.ticker) && (
              <span className="ml-1">{krxName(cluster.ticker)}</span>
            )}
          </span>
        )}
        <span>아이템 {cluster.item_count}건</span>
        {cluster.sentiment ? (
          <span className="flex items-center gap-1.5">
            감성 <SentimentBadge value={cluster.sentiment} />
            {cluster.sentiment_score !== undefined && cluster.sentiment_score !== null && (
              <SentimentScoreCell value={cluster.sentiment_score} />
            )}
          </span>
        ) : (
          cluster.sentiment_avg !== undefined && cluster.sentiment_avg !== null && (
            <span>
              평균 감성{' '}
              <SentimentAvgCell value={cluster.sentiment_avg} />
            </span>
          )
        )}
        <span>최초 {formatTime(cluster.first_seen_at)}</span>
        <span>최근 {formatTime(cluster.last_seen_at)}</span>
        {cluster.summarized_at && (
          <span title={formatTime(cluster.summarized_at)}>
            요약 {formatRelativeKST(cluster.summarized_at)}
          </span>
        )}
      </div>

      {cluster.summary_ko && (
        <div className="rounded-md border border-border bg-card p-3 text-sm">
          <div className="mb-1 text-xs font-semibold text-muted-foreground">
            클러스터 요약
          </div>
          <p className="text-foreground leading-relaxed">{cluster.summary_ko}</p>
        </div>
      )}

      {cluster.quality_score !== undefined && cluster.quality_score !== null && (
        <div className="rounded-md border border-border bg-card p-3 text-sm">
          <div className="mb-1 flex items-center gap-2 text-xs font-semibold text-muted-foreground">
            <span>품질 평가 (LLM judge)</span>
            <QualityBadge score={cluster.quality_score} />
            <VerdictBadge
              verdict={cluster.quality_verdict}
              reason={cluster.quality_reason}
            />
          </div>
          {cluster.quality_reason && (
            <p className="text-foreground leading-relaxed">
              {cluster.quality_reason}
            </p>
          )}
          {cluster.quality_evaluated_at && (
            <p className="mt-1 text-[11px] text-muted-foreground">
              평가 {formatRelativeKST(cluster.quality_evaluated_at)} ·{' '}
              {formatTime(cluster.quality_evaluated_at)}
            </p>
          )}
        </div>
      )}

      {cluster.label && (
        <div className="rounded-md border border-info-soft bg-info-soft p-2 text-sm text-info-soft-foreground">
          <span className="font-semibold mr-2">라벨</span>
          {cluster.label}
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-muted-foreground">멤버가 없습니다.</p>
      ) : (
        <ul className="space-y-2">
          {items.map((m) => (
            <li
              key={m.id}
              className="rounded-md border border-border bg-card p-2"
            >
              <div className="mb-1 flex flex-wrap items-center gap-2 text-xs">
                <Badge variant="muted" shape="square">
                  {m.service}
                </Badge>
                {m.sentiment && <SentimentBadge value={m.sentiment} />}
                {m.ticker && (
                  <span className="font-mono text-muted-foreground">
                    {m.ticker}
                  </span>
                )}
                <span
                  className="ml-auto text-muted-foreground"
                  title={formatTime(m.published_at || m.captured_at)}
                >
                  {formatRelativeKST(m.published_at || m.captured_at)}
                </span>
              </div>
              <h4 className="text-sm font-medium leading-snug">
                {m.url ? (
                  <a
                    href={m.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:underline"
                  >
                    {m.title}
                  </a>
                ) : (
                  m.title
                )}
              </h4>
              {m.origin && (
                <p className="mt-1 text-[11px] text-muted-foreground">
                  origin={m.origin}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function SentimentBadge({ value }: { value: string }) {
  const map: Record<string, { label: string; variant: 'success' | 'destructive' | 'muted' | 'warning' }> = {
    bullish: { label: '강세', variant: 'success' },
    bearish: { label: '약세', variant: 'destructive' },
    neutral: { label: '중립', variant: 'muted' },
    mixed: { label: '혼조', variant: 'warning' },
  }
  const m = map[value] ?? { label: value, variant: 'muted' as const }
  return (
    <Badge variant={m.variant} shape="pill">
      {m.label}
    </Badge>
  )
}

function scopeVariant(scope: NewsCluster['scope']): 'success' | 'info' | 'warning' {
  switch (scope) {
    case 'ticker':
      return 'success'
    case 'market':
      return 'info'
    case 'crypto':
      return 'warning'
  }
}
